export class CookieUtil {

  /**
   * Retrieves the value of a cookie by name.
   * @param name - The name of the cookie to retrieve.
   * @returns The decoded cookie value, or null if not found.
   */
  public static get(name: string): string | null {
    // Escape special characters in the cookie name for use in RegExp
    const escapedName = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');

    // Build a pattern to match the cookie's name and capture its value
    const pattern = new RegExp('(?:^|; )' + escapedName + '=([^;]*)');

    // Attempt to find a match in document.cookie
    const match = document.cookie.match(pattern);

    // If a match is found, decode and return the cookie value
    return match ? decodeURIComponent(match[1]) : null;
  }

  /**
   * Sets a cookie with various optional settings.
   * @param name - The name of the cookie.
   * @param value - The value to store in the cookie.
   * @param options - Optional settings for the cookie (maxAge, path, domain, secure, sameSite).
   */
  public static set(
    name: string,
    value: string,
    options: {
      maxAge?: number;                      // Lifetime of the cookie in seconds
      path?: string;                        // URL path the cookie is valid for
      domain?: string;                      // Domain cookie is valid for
      secure?: boolean;                     // Whether to send cookie only over HTTPS
      sameSite?: 'Lax' | 'Strict' | 'None'; // CSRF protection setting
    } = {}
  ): void {
    // Start with basic name=value pair, both URL-encoded
    let cookieStr = encodeURIComponent(name) + '=' + encodeURIComponent(value);

    // Append Max-Age if provided
    if (options.maxAge !== undefined) {
      cookieStr += `; Max-Age=${options.maxAge}`;
    }

    // Append Path if provided, default could be '/'
    if (options.path) {
      cookieStr += `; Path=${options.path}`;
    }

    // Append Domain if provided (e.g. '.example.com')
    if (options.domain) {
      cookieStr += `; Domain=${options.domain}`;
    }

    // Append Secure flag if HTTPS-only cookie is desired
    if (options.secure) {
      cookieStr += `; Secure`;
    }

    // Append SameSite attribute for CSRF protection
    if (options.sameSite) {
      cookieStr += `; SameSite=${options.sameSite}`;
    }

    // Assign the constructed string to document.cookie
    document.cookie = cookieStr;
  }

  /**
   * Deletes a cookie by setting its Max-Age to 0.
   * @param name - The name of the cookie to delete.
   * @param path - The path the cookie was set on (default '/').
   */
  public static delete(name: string, path: string = '/') {
    // Overwrite the cookie with an expired Max-Age to remove it
    document.cookie =
      encodeURIComponent(name) +
      '=; Max-Age=0; Path=' +
      path +
      '; SameSite=Lax';
  }
}
